export interface BoundingBox {
  Width: number;
  Height: number;
  Left: number;
  Top: number;
}

export interface PolygonCoord {
  X: number;
  Y: number;
}

export interface FieldDataPoint {
  boundingBox?: BoundingBox;
  polygon?: PolygonCoord[];
  confidence?: number;
  page?: number;
}

export interface NormalizedPolygon {
  confidence: number;
  polygon: PolygonCoord[];
  page: number;
}

export interface ImageMetrics {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

const CONFIDENCE_GREEN = 90;
const CONFIDENCE_ORANGE_MIN = 30;
const COLOR_GREEN = 'green';
const COLOR_ORANGE = '#D35400';
const COLOR_RED = '#950606';

export const getConfidenceColor = (confidence: number): string => {
  if (confidence > CONFIDENCE_GREEN) {
    return COLOR_GREEN;
  }
  if (confidence >= CONFIDENCE_ORANGE_MIN) {
    return COLOR_ORANGE;
  }

  return COLOR_RED;
};

const boundingBoxToPolygon = (box: BoundingBox): PolygonCoord[] => {
  const {Left, Top, Width, Height} = box;

  return [
    {X: Left, Y: Top},
    {X: Left + Width, Y: Top},
    {X: Left + Width, Y: Top + Height},
    {X: Left, Y: Top + Height}
  ];
};

const resolvePolygon = (point: FieldDataPoint): PolygonCoord[] | null => {
  if (point.polygon && point.polygon.length > 0) {
    return point.polygon;
  }
  if (point.boundingBox) {
    return boundingBoxToPolygon(point.boundingBox);
  }

  return null;
};

export const normalizeFieldDataPoints = (
  points?: FieldDataPoint[] | null
): NormalizedPolygon[] => {
  if (!points?.length) {
    return [];
  }

  return points.reduce<NormalizedPolygon[]>((acc, point) => {
    const polygon = resolvePolygon(point);
    if (!polygon) {
      return acc;
    }

    acc.push({
      confidence: point.confidence ?? 0,
      polygon,
      page: point.page ?? 1
    });

    return acc;
  }, []);
};

export const isPdfDocument = (
  location?: string,
  fileName?: string
): boolean => {
  const normalizedLocation = location?.toLowerCase() ?? '';
  const normalizedFileName = fileName?.toLowerCase() ?? '';

  return (
    normalizedLocation.endsWith('.pdf') ||
    normalizedLocation.startsWith('data:application/pdf') ||
    normalizedFileName.endsWith('.pdf')
  );
};

export const getRenderedImageMetrics = (
  img: HTMLImageElement
): ImageMetrics => {
  const rect = img.getBoundingClientRect();
  const {naturalWidth, naturalHeight} = img;

  if (!naturalWidth || !naturalHeight || !rect.width || !rect.height) {
    return {width: 0, height: 0, offsetX: 0, offsetY: 0};
  }

  const containerWidth = rect.width;
  const containerHeight = rect.height;
  const naturalAspect = naturalWidth / naturalHeight;
  const containerAspect = containerWidth / containerHeight;
  const objectFit = getComputedStyle(img).objectFit;

  if (objectFit === 'contain' || objectFit === 'scale-down') {
    if (naturalAspect > containerAspect) {
      const renderWidth = containerWidth;
      const renderHeight = containerWidth / naturalAspect;

      return {
        width: renderWidth,
        height: renderHeight,
        offsetX: 0,
        offsetY: (containerHeight - renderHeight) / 2
      };
    }

    const renderHeight = containerHeight;
    const renderWidth = containerHeight * naturalAspect;

    return {
      width: renderWidth,
      height: renderHeight,
      offsetX: (containerWidth - renderWidth) / 2,
      offsetY: 0
    };
  }

  return {
    width: containerWidth,
    height: containerHeight,
    offsetX: 0,
    offsetY: 0
  };
};
